import type { Label, Project, ProjectColor } from '@timato/shared';
import { uuidv7 } from '@timato/shared';
import { db } from '@/db/db';
import { deleteRow, notDeleted, saveRow } from './repo';
import { isWork } from './stats';
import { updateProject } from './projects';

/** Personal label (Phase 1). Team labels (teamId set) arrive with Phase 3 and use the same functions. */
export async function createLabel(name: string, tint: ProjectColor | null = null): Promise<Label> {
  const position = await db.labels.count();
  return saveRow('labels', { id: uuidv7(), teamId: null, name: name.trim(), tint, position, retiredAt: null, updatedAt: '', deletedAt: null });
}
export const updateLabel = (l: Label, patch: Partial<Pick<Label, 'name' | 'tint'>>) => saveRow('labels', { ...l, ...patch });
/** Projects a personal label can be given to from Settings › Labels: your own, still open. Team projects take team labels. */
export const takesPersonalLabels = (p: Project) => !p.teamId && !p.archivedAt && !p.deletedAt;

/**
 * Give a label to exactly these projects, among the ones that take personal labels. Only a project whose set changes is
 * written; the project's sessions from now on inherit it (8e), past ones keep what they were logged with.
 */
export async function setLabelProjects(labelId: string, projectIds: string[]) {
  const projects = (await db.projects.toArray()).filter(takesPersonalLabels);
  await Promise.all(projects.flatMap((p) => {
    const has = p.labelIds.includes(labelId), want = projectIds.includes(p.id);
    return has === want ? [] : [updateProject(p, { labelIds: want ? [...p.labelIds, labelId] : p.labelIds.filter((id) => id !== labelId) })];
  }));
}

export const retireLabel = (l: Label) => saveRow('labels', { ...l, retiredAt: new Date().toISOString() });
export const unretireLabel = (l: Label) => saveRow('labels', { ...l, retiredAt: null });

/**
 * The labels that belong to a project (TW-3): the ones its sessions inherit, plus every label already used on one of
 * its tasks or sessions. Nothing stores that a label "is in" a project, so labelling a task is what puts the label
 * there. With `null`, the labels that belong to no project: used on sessions without one, or on nothing yet.
 */
export async function projectLabelIds(project: Pick<Project, 'id' | 'labelIds'> | null): Promise<Set<string>> {
  const used = (rows: { labelIds: string[]; deletedAt: string | null }[]) => rows.filter(notDeleted).flatMap((r) => r.labelIds);
  if (project) {
    const [tasks, sessions] = await Promise.all([db.tasks.where('projectId').equals(project.id).toArray(), db.sessions.where('projectId').equals(project.id).toArray()]);
    return new Set([...project.labelIds, ...used(tasks), ...used(sessions)]);
  }
  // No index answers "projectId is null" (IndexedDB leaves nulls out), so this one reads everything.
  const [labels, projects, tasks, sessions] = await Promise.all([db.labels.toArray(), db.projects.toArray(), db.tasks.toArray(), db.sessions.toArray()]);
  const inProjects = new Set([...used(projects), ...used(tasks), ...used(sessions.filter((s) => s.projectId))]);
  return new Set([...labels.map((l) => l.id).filter((id) => !inProjects.has(id)), ...used(sessions.filter((s) => !s.projectId))]);
}

/** Find an existing label by name, case-insensitively, or create it. Keeps the picker's "Create" idempotent. */
export async function findOrCreateLabel(name: string): Promise<Label> {
  const key = name.trim().toLowerCase();
  const existing = (await db.labels.toArray()).find((l) => notDeleted(l) && l.name.trim().toLowerCase() === key);
  return existing ?? createLabel(name);
}

const swap = (ids: string[], from: string, to: string) => [...new Set(ids.map((id) => (id === from ? to : id)))];

/** Merge `source` into `target`: re-point every project, task and session, then tombstone the source. */
export async function mergeLabels(source: Label, target: Label) {
  if (source.id === target.id) return;
  const [projects, tasks, sessions] = await Promise.all([db.projects.toArray(), db.tasks.toArray(), db.sessions.toArray()]);
  await db.transaction('rw', [db.projects, db.tasks, db.sessions, db.labels, db.outbox], async () => {
    for (const p of projects) if (p.labelIds.includes(source.id)) await saveRow('projects', { ...p, labelIds: swap(p.labelIds, source.id, target.id) });
    for (const t of tasks) if (t.labelIds.includes(source.id)) await saveRow('tasks', { ...t, labelIds: swap(t.labelIds, source.id, target.id) });
    for (const s of sessions) if (s.labelIds.includes(source.id)) await saveRow('sessions', { ...s, labelIds: swap(s.labelIds, source.id, target.id) });
    await deleteRow('labels', source);
  });
}

/** Hard-ish delete (tombstone) only when nothing references the label; otherwise callers should retire. */
export async function deleteUnusedLabel(l: Label): Promise<boolean> {
  const used = (await usage()).get(l.id);
  if (used && (used.sessions > 0 || used.tasks > 0 || used.projects > 0)) return false;
  await deleteRow('labels', l);
  return true;
}

export type LabelUsage = { sessions: number; seconds: number; tasks: number; projects: number };

/** How much each label carries, for Settings › Labels and merge/retire copy. */
export async function usage(): Promise<Map<string, LabelUsage>> {
  const [projects, tasks, sessions] = await Promise.all([db.projects.toArray(), db.tasks.toArray(), db.sessions.toArray()]);
  const m = new Map<string, LabelUsage>();
  const at = (id: string) => m.get(id) ?? (m.set(id, { sessions: 0, seconds: 0, tasks: 0, projects: 0 }), m.get(id)!);
  for (const p of projects.filter(notDeleted)) for (const id of p.labelIds) at(id).projects++;
  for (const t of tasks.filter(notDeleted)) for (const id of t.labelIds) at(id).tasks++;
  for (const s of sessions.filter((s) => notDeleted(s) && isWork(s))) for (const id of s.labelIds) { const u = at(id); u.sessions++; u.seconds += s.durationSec; }
  return m;
}
